import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';

import GameModal from '../GameModal/GameModal';
import CharacterAnimation from '../CharacterAnimation/CharacterAnimation';
import classes from './ActivityResults.module.scss';
import {
  setActivityPartial,
  setActivityMastered,
  setActivityAssessmentStats,
} from 'actions/flinkPlayActions';
import { restartActivity, showStats } from 'actions/gameActions';
import { playApplause, stopAudio } from 'actions/audioActions';
import { formatHtmlForPlay, isMobileAppRuntime } from 'utils';
import api from 'api';
import { buildAssessmentSubmissions } from 'utils/desktopAssessment';
import {
  PlayAgainIcon,
  MenuIcon,
  StarIcon,
} from 'components/flink-play/FlinkPlayIcons/FlinkPlayIcons';

const DEFAULT_STATE = {
  title: '',
  translate: null,
  wrapperStyles: {},
  formattedHtml: '',
  dialogVisible: false,
  characterAnimationVisible: false,
};

function isCurrentGroupInsideAssessmentBridge(desktopAssessmentBridge, currentActivityGroup) {
  if (!desktopAssessmentBridge?.enabled || !currentActivityGroup) return false;

  const ids = (desktopAssessmentBridge.assessmentGroupIds || []).map(String);

  if (ids.length) {
    return ids.includes(String(currentActivityGroup));
  }

  return !!desktopAssessmentBridge.freeTrialGroupId &&
    String(currentActivityGroup) === String(desktopAssessmentBridge.freeTrialGroupId);
}

function isActivityInsideAssessmentBridge(desktopAssessmentBridge, activityId) {
  if (!desktopAssessmentBridge?.enabled || !activityId) return false;

  return (desktopAssessmentBridge.assessmentLeafActivityIds || [])
    .map(String)
    .includes(String(activityId));
}


function getAssessmentActivityKeyFromBridge(desktopAssessmentBridge, currentActivity) {
  if (!desktopAssessmentBridge?.enabled) return null;

  const activityId = currentActivity?.activity?._id || currentActivity?._id;
  if (!activityId) return null;

  const sourceId = String(activityId);
  const menuItem = (desktopAssessmentBridge.attempt?.assessmentMenu || [])
    .find((item) => String(item?.sourceActivityId || '') === sourceId);

  if (menuItem?.activityKey) return menuItem.activityKey;

  const slot = (desktopAssessmentBridge.attempt?.assessmentActivitySlots || [])
    .find((item) => String(item?.sourceActivityId || '') === sourceId);

  if (slot?.activityKey) return slot.activityKey;

  const leafIds = (desktopAssessmentBridge.assessmentLeafActivityIds || []).map(String);
  const index = leafIds.indexOf(sourceId);
  if (index === -1) return null;

  return (desktopAssessmentBridge.expectedActivityKeys || [])[index] || null;
}

function unwrapMobileAssessmentAttempt(response) {
  if (!response) return null;

  return (
    response.attempt ||
    response.data?.attempt ||
    response.data?.data?.attempt ||
    response.data?.data ||
    response.data ||
    response
  );
}

function findAssessmentResult(attempt, activityKey) {
  if (!activityKey || !Array.isArray(attempt?.activityResults)) return null;

  return attempt.activityResults.find((item) => item?.activityKey === activityKey) || null;
}

function getMasteredFromAssessmentResult(result) {
  if (!result?.star) return null;

  if (result.star === 'green') return true;
  if (result.star === 'yellow') return false;

  return null;
}

function hasFailedWordReadingBlock(answersByQuestion = []) {
  if (!Array.isArray(answersByQuestion) || !answersByQuestion.length) return false;

  let start = 0;
  let blockIndex = 0;

  while (start < answersByQuestion.length) {
    const isFinalBlock = blockIndex >= 3;
    const remaining = answersByQuestion.length - start;
    const blockSize = isFinalBlock ? remaining : Math.min(3, remaining);
    const block = answersByQuestion.slice(start, start + blockSize);
    const correct = block.reduce((sum, value) => sum + Number(value || 0), 0);

    if (correct < block.length) return true;

    start += blockSize;
    blockIndex += 1;
  }

  return false;
}

function resolveAssessmentResultPreview(activityKey, assessmentStatsPayload, fallbackIsMastered) {
  if (activityKey !== 'phonics_4_word_reading') {
    return {
      isMastered: fallbackIsMastered,
      star: fallbackIsMastered ? 'green' : 'yellow',
    };
  }

  const answersByQuestion = assessmentStatsPayload?.rawAssessment?.answersByQuestion;
  const correctAnswers = Number(assessmentStatsPayload?.correctAnswers);
  const totalQuestions = Number(assessmentStatsPayload?.totalQuestions);
  const hasFailedBlock = Array.isArray(answersByQuestion) && answersByQuestion.length
    ? hasFailedWordReadingBlock(answersByQuestion)
    : !(Number.isFinite(correctAnswers) && Number.isFinite(totalQuestions) && totalQuestions > 0 && correctAnswers >= totalQuestions);
  const isMastered = !hasFailedBlock;

  return {
    isMastered,
    star: isMastered ? 'green' : 'yellow',
  };
}

class ActivityResults extends Component {
  state = { ...DEFAULT_STATE };


  componentDidMount() {
    if (this.props.stats) {
      this.initStats();
    }
  }

  componentDidUpdate(prevProps) {
    const { stats } = this.props;

    if (!prevProps.stats && stats) {
      this.initStats();
    } else if (prevProps.stats && !stats) {
      stopAudio();
      this.setState(DEFAULT_STATE);
    }
  }

  shouldSuppressPaidStar = () => {
    const { desktopAssessmentBridge, currentActivity, currentActivityGroup } = this.props;

    if (!desktopAssessmentBridge?.enabled) {
      return false;
    }

    const activityId = currentActivity?.activity?._id;
    const suppressedIds = (desktopAssessmentBridge.suppressedActivityIds || []).map(String);

    return (
      isCurrentGroupInsideAssessmentBridge(desktopAssessmentBridge, currentActivityGroup) ||
      isActivityInsideAssessmentBridge(desktopAssessmentBridge, activityId) ||
      (!!activityId && suppressedIds.includes(String(activityId)))
    );
  };

  submitAssessmentBeforeResults = async ({
    activityKey,
    assessmentStatsPayload,
    isMastered,
    stats,
  }) => {
    const {
      desktopAssessmentBridge,
      currentActivity,
      currentActivityGroup,
    } = this.props;

    if (!desktopAssessmentBridge?.enabled || !desktopAssessmentBridge?.attemptId || !activityKey) {
      return null;
    }

    const existingResult = findAssessmentResult(desktopAssessmentBridge.attempt, activityKey);

    if (existingResult) {
      return existingResult;
    }

    const currentActivityDoc = currentActivity?.activity || currentActivity || {};
    const sourceActivityId = currentActivityDoc?._id || currentActivity?._id || null;
    const submissions = buildAssessmentSubmissions({
      logicalKeys: [activityKey],
      stats: {
        ...(stats || {}),
        data: {
          ...((stats || {}).data || {}),
          ...(assessmentStatsPayload || {}),
        },
      },
      activityProgress: {
        mastered: isMastered,
        assessmentStats: assessmentStatsPayload,
      },
      currentActivity,
      isMastered,
    });

    const submission = submissions.find((item) => item?.activityKey === activityKey);

    if (!submission) {
      return null;
    }

    const requestBody = {
      activityKey: submission.activityKey,
      payload: {
        ...submission.payload,
        sourceActivityId,
        sourceActivityGroupId: currentActivityGroup || null,
      },
      sourceActivityId,
      sourceActivityGroupId: currentActivityGroup || null,
      allowReplay: true,
    };

    try {
      console.info('[desktop-assessment][results-submit-start]', {
        attemptId: desktopAssessmentBridge.attemptId,
        activityKey,
        requestBody,
      });

      const response = await api.mobileAssessment.submitActivity(desktopAssessmentBridge.attemptId, requestBody);
      const attempt = unwrapMobileAssessmentAttempt(response);
      const result = findAssessmentResult(attempt, activityKey);

      console.info('[desktop-assessment][results-submit-success]', {
        attemptId: attempt?._id || desktopAssessmentBridge.attemptId,
        activityKey,
        star: result?.star || null,
        assignedLevel: result?.assignedLevel || null,
      });

      if (desktopAssessmentBridge.onAttemptUpdated && attempt?._id) {
        desktopAssessmentBridge.onAttemptUpdated({
          attemptId: attempt._id,
          attempt,
          reportSnapshot: attempt.reportSnapshot || null,
        });
      }

      return result;
    } catch (error) {
      console.error('[desktop-assessment][results-submit-error]', {
        attemptId: desktopAssessmentBridge.attemptId,
        activityKey,
        error,
      });

      return null;
    }
  };

  initStats = async () => {
    const {
      isPreview,
      stats,
      learningCenter,
      solutionTranslate,
      generalTranslate,
      placementTranslate,
      setActivityPartial,
      setActivityMastered,
      setActivityAssessmentStats,
      isWordlistActivity,
    } = this.props;

    const translate = placementTranslate || solutionTranslate || generalTranslate;

    const {
      isCongratulations = false,
      predefinedMastered = false,
      skipCharacterAnimation = false,
      withScore = false,
      html: originHtml,
      data = {},
      ebookBenchmark,
    } = stats;

    const title = translate(10, 'Activity Results');

    let score;
    let percentAnswersForMaster;

    if (withScore) {
      const percent =
        learningCenter &&
        learningCenter.config &&
        learningCenter.config.percentAnswers;

      percentAnswersForMaster = parseInt(percent || '50%');

      const { allProblemsCount, problemsAnsweredCorrectly } = data;
      score = Math.ceil((problemsAnsweredCorrectly / allProblemsCount) * 100);
    }

    const isMastered =
      (withScore && score >= percentAnswersForMaster) ||
      (ebookBenchmark && ebookBenchmark.speed >= ebookBenchmark.benchmark) ||
      isCongratulations ||
      predefinedMastered;
    const suppressPaidStar = this.shouldSuppressPaidStar();
    const normalizedCorrectAnswers = Number.isFinite(Number(data?.problemsAnsweredCorrectly))
      ? Number(data.problemsAnsweredCorrectly)
      : Number.isFinite(Number(data?.correctAnswers))
        ? Number(data.correctAnswers)
        : null;
    const normalizedTotalQuestions = Number.isFinite(Number(data?.allProblemsCount))
      ? Number(data.allProblemsCount)
      : Number.isFinite(Number(data?.totalQuestions))
        ? Number(data.totalQuestions)
        : null;
    const baseAssessmentStatsPayload = {
      ...(data || {}),
      problemsAnsweredCorrectly: normalizedCorrectAnswers,
      correctAnswers: normalizedCorrectAnswers,
      allProblemsCount: normalizedTotalQuestions,
      totalQuestions: normalizedTotalQuestions,
      rawAssessment: {
        ...((data || {}).rawAssessment || {}),
        correctAnswers: normalizedCorrectAnswers,
        totalQuestions: normalizedTotalQuestions,
        answersByQuestion: Array.isArray(data?.rawAssessment?.answersByQuestion)
          ? data.rawAssessment.answersByQuestion
          : Array.isArray(data?.answersByQuestion)
            ? data.answersByQuestion
            : undefined,
      },
      score,
      isMastered,
      ebookBenchmark: ebookBenchmark || null,
    };
    const activityKey = getAssessmentActivityKeyFromBridge(
      this.props.desktopAssessmentBridge,
      this.props.currentActivity,
    );
    const assessmentResultPreview = resolveAssessmentResultPreview(
      activityKey,
      baseAssessmentStatsPayload,
      isMastered,
    );
    const backendAssessmentResult = !isPreview
      ? await this.submitAssessmentBeforeResults({
        activityKey,
        assessmentStatsPayload: baseAssessmentStatsPayload,
        isMastered,
        stats,
      })
      : null;
    const backendMastered = getMasteredFromAssessmentResult(backendAssessmentResult);
    const displayedIsMastered = typeof backendMastered === 'boolean'
      ? backendMastered
      : assessmentResultPreview.isMastered;
    const displayedAssessmentStar = backendAssessmentResult?.star ||
      assessmentResultPreview.star ||
      (displayedIsMastered ? 'green' : 'yellow');
    const assessmentStatsPayload = {
      ...baseAssessmentStatsPayload,
      isMastered: displayedIsMastered,
      assessmentStar: displayedAssessmentStar,
      assignedLevel: backendAssessmentResult?.assignedLevel ?? baseAssessmentStatsPayload.assignedLevel ?? null,
      backendAssessmentResult: backendAssessmentResult || null,
      rawAssessment: {
        ...(baseAssessmentStatsPayload.rawAssessment || {}),
        assessmentStar: displayedAssessmentStar,
        assignedLevel: backendAssessmentResult?.assignedLevel ?? baseAssessmentStatsPayload.rawAssessment?.assignedLevel ?? null,
      },
    };

    if (!isPreview) {
      setActivityAssessmentStats(assessmentStatsPayload);
    }

    if (!isPreview && !suppressPaidStar) {
      if (displayedIsMastered) {
        setActivityMastered({ assessmentStats: assessmentStatsPayload });
      } else {
        setActivityPartial({ assessmentStats: assessmentStatsPayload });
      }
    } else if (suppressPaidStar) {

    }

    const wrapperStyles = {
      fontSize: !originHtml && '2vmin',
    };

    let stringNumber;

    if (!originHtml) {
      if (typeof score === 'number') {
        stringNumber = isWordlistActivity ? 349 : displayedIsMastered ? 346 : 347;
      } else if (isCongratulations) {
        stringNumber = 351;
      } else if (ebookBenchmark) {
        stringNumber = isMastered ? 633 : 503;
      }
    }

    const html = stringNumber
      ? translate(stringNumber, undefined, undefined, true)
      : originHtml;

    const formattedHtml = formatHtmlForPlay({ html, data: { ...data, score } });

    this.setState(
      {
        title,
        translate,
        wrapperStyles,
        formattedHtml,
        isMastered,
        assessmentDisplayedIsMastered: displayedIsMastered,
        score,
      },
      () => {
        (displayedIsMastered && !skipCharacterAnimation
          ? this.showCharacterAnimation
          : this.showDialog)();
      }
    );
  };

  showDialog = () =>
    this.setState({ dialogVisible: true, characterAnimationVisible: false });

  showCharacterAnimation = () =>
    this.setState({ characterAnimationVisible: true });

  isDisplayedMastered = () =>
    typeof this.state.assessmentDisplayedIsMastered === 'boolean'
      ? this.state.assessmentDisplayedIsMastered
      : this.state.isMastered;

  onCharacterReady = () => {
    if (this.isDisplayedMastered()) {
      this.props.playApplause(true);
    }
  };

  onNoCharacter = () => {
    if (this.isDisplayedMastered()) {
      this.props.playApplause();
    }

    this.showDialog();
  };

  render() {
    const {
      history,
      gameGoBackHandler,
      restartActivity,
      stats,
      showStats,
    } = this.props;

    const {
      title,
      translate,
      wrapperStyles,
      formattedHtml,
      isMastered,
      assessmentDisplayedIsMastered,
      score,
      dialogVisible,
      characterAnimationVisible,
    } = this.state;

    if (characterAnimationVisible) {
      return (
        <CharacterAnimation
          onReady={this.onCharacterReady}
          onFinish={this.showDialog}
          onError={this.showDialog}
          onNoCharacter={this.onNoCharacter}
        />
      );
    }

    if (!dialogVisible) return null;

    const displayedStarIsMastered = typeof assessmentDisplayedIsMastered === 'boolean'
      ? assessmentDisplayedIsMastered
      : isMastered;

    const {
      onlyCloseButton,
      data = {},
      withScore,
      compactMobile = false,
    } = stats || {};
    const { allProblemsCount, problemsAnsweredCorrectly } = data;
    const mobileRuntime = isMobileAppRuntime();
    const compactMobileResult = mobileRuntime && (withScore || compactMobile);

    return (
      <GameModal
        centerButtons
        outerClassnames={mobileRuntime ? [classes.mobileModal] : []}
        innerClassnames={mobileRuntime ? [classes.mobileModalInner] : []}
        contentClassnames={mobileRuntime ? [classes.mobileModalContent] : []}
        buttons={
          onlyCloseButton
            ? [
                {
                  title: translate(80, 'Close'),
                  clickHandler: () => showStats(null),
                },
              ]
            : []
        }
      >
        <div
          className={`${classes.wrapper} ${compactMobileResult ? classes.mobileScoreWrapper : ''}`}
          style={wrapperStyles}
        >
          <div className={classes.title}>
            <span>{title}:</span>
            <StarIcon mastered={displayedStarIsMastered} partial />
          </div>

          {!withScore && (
            <div dangerouslySetInnerHTML={{ __html: formattedHtml }}></div>
          )}

          {withScore && (
            <>
              <div
                className={`${classes.activityScoreProgress} ${
                  compactMobileResult ? classes.mobileScoreProgress : ''
                }`}
              >
                {_.times(allProblemsCount, Number).map((num, idx) => (
                  <span
                    key={num}
                    className={
                      idx < problemsAnsweredCorrectly
                        ? classes.progressFilled
                        : ''
                    }
                  />
                ))}
              </div>

              <div className={classes.stats}>
                {translate(678, 'Score: ')} {problemsAnsweredCorrectly}/
                {allProblemsCount} ({score})%
              </div>
            </>
          )}

          {!onlyCloseButton && (
            <div className={classes.imageButtons}>
              <button onClick={restartActivity}>
                <PlayAgainIcon />
                <span>{translate(304, 'Play Again', true)}</span>
              </button>
              <button onClick={gameGoBackHandler || history.goBack}>
                <MenuIcon />
                <span>{translate(174, 'Menu', true)}</span>
              </button>
            </div>
          )}
        </div>
      </GameModal>
    );
  }
}

ActivityResults.propTypes = {
  isPreview: PropTypes.bool,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  playApplause: PropTypes.func.isRequired,
  restartActivity: PropTypes.func.isRequired,
  solutionTranslate: PropTypes.func,
  generalTranslate: PropTypes.func.isRequired,
  placementTranslate: PropTypes.func,
  gameGoBackHandler: PropTypes.func,
  showStats: PropTypes.func.isRequired,
  setActivityPartial: PropTypes.func.isRequired,
  setActivityMastered: PropTypes.func.isRequired,
  setActivityAssessmentStats: PropTypes.func.isRequired,
  desktopAssessmentBridge: PropTypes.object,
  currentActivity: PropTypes.object,
  currentActivityGroup: PropTypes.string,
  solutionMenu: PropTypes.object,
};

const mapStateToProps = ({ game, flinkPlay, status }) => ({
  isPreview: game.isPreview,
  isWordlistActivity: !!game.currentActivity.activity.generatedFrom,
  stats: game.stats,
  gameGoBackHandler: game.goBackHandler,
  generalTranslate: status.translate,
  solutionTranslate: flinkPlay.solutionTranslate,
  learningCenter: flinkPlay.learningCenter,
  currentActivity: game.currentActivity,
  currentActivityGroup: flinkPlay.currentActivityGroup,
  solutionMenu: flinkPlay.solutionMenu,
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    restartActivity,
    playApplause,
    showStats,
    setActivityPartial,
    setActivityMastered,
    setActivityAssessmentStats,
  })
)(ActivityResults);
